import React, { useState, useEffect, useRef, useCallback } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import { gsap } from 'gsap'

import './_productSlider.scss'

const ProductSlider = ({ items }) => {
  const [activeProduct, setActiveProduct] = useState(0)
  const [activePage, setActivePage] = useState(0)
  const [visibleProducts, setVisibleProducts] = useState(4)
  const [totalPages, setTotalPages] = useState(1)
  const sliderRef = useRef(null)
  const productsRef = useRef([])

  // Calculate visible products and total pages based on screen size
  useEffect(() => {
    const calculateLayout = () => {
      const windowWidth = window.innerWidth
      let visible = 4

      if (windowWidth < 480) {
        visible = 1 // Show 1 full item on mobile
      } else if (windowWidth < 960  ) {
        visible = 2 // Show 2 full items on small tablet
      } else if (windowWidth < 1200) {
        visible = 3 // Show 3 full items on tablet
      } else {
        visible = 4 // Show 4 full items on desktop
      }

      setVisibleProducts(visible)
      
      // Calculate total pages based on visible products
      // For fractional visible products (like 1.2, 2.5), we need to account for the sliding behavior
      const fullVisible = Math.floor(visible)
      const pages = Math.max(1, items.length - fullVisible + 1)
      setTotalPages(pages)
    }

    calculateLayout()
    window.addEventListener('resize', calculateLayout)
    return () => window.removeEventListener('resize', calculateLayout)
  }, [items.length])

  // Handle dot navigation click - navigate to specific page
  const goToPage = useCallback((pageIndex) => {
    if (pageIndex === activePage) return

    const containerWidth = sliderRef.current.offsetWidth
    const itemWidth = containerWidth / visibleProducts
    const totalWidth = items.length * itemWidth
    
    // Calculate the maximum allowed slide position
    const maxSlide = Math.max(0, totalWidth - containerWidth)
    const requestedSlide = pageIndex * itemWidth
    
    // Use the smaller of the two: requested position or maximum allowed
    const translateX = -Math.min(requestedSlide, maxSlide)

    gsap.to(sliderRef.current, {
      duration: 0.3,
      x: translateX,
      ease: 'power2.out'
    })

    setActivePage(pageIndex)
    setActiveProduct(pageIndex) // Keep this for backward compatibility with other functions
  }, [activePage, visibleProducts, items.length])

  // Keep the old function for internal use (keyboard/touch navigation)
  const goToProduct = useCallback((productIndex) => {
    const pageIndex = Math.min(productIndex, totalPages - 1)
    goToPage(pageIndex)
  }, [totalPages, goToPage])

  // Touch/swipe handling
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)

  const handleTouchStart = (e) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    // Calculate the maximum product index we can navigate to
    const containerWidth = sliderRef.current.offsetWidth
    const itemWidth = containerWidth / visibleProducts
    const totalWidth = items.length * itemWidth
    const maxSlide = Math.max(0, totalWidth - containerWidth)
    const maxProductIndex = Math.floor(maxSlide / itemWidth)

    if (isLeftSwipe && activeProduct < Math.min(items.length - 1, maxProductIndex)) {
      goToProduct(activeProduct + 1)
    }
    if (isRightSwipe && activeProduct > 0) {
      goToProduct(activeProduct - 1)
    }
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!sliderRef.current) return

      // Calculate the maximum product index we can navigate to
      const containerWidth = sliderRef.current.offsetWidth
      const itemWidth = containerWidth / visibleProducts
      const totalWidth = items.length * itemWidth
      const maxSlide = Math.max(0, totalWidth - containerWidth)
      const maxProductIndex = Math.floor(maxSlide / itemWidth)

      if (e.key === 'ArrowLeft' && activeProduct > 0) {
        goToProduct(activeProduct - 1)
      }
      if (e.key === 'ArrowRight' && activeProduct < Math.min(items.length - 1, maxProductIndex)) {
        goToProduct(activeProduct + 1)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [activeProduct, items.length, visibleProducts, goToProduct])

  // Track which page is active based on scroll position
  useEffect(() => {
    const sliderElement = sliderRef.current
    
    const handleScroll = () => {
      if (!sliderElement) return

      // Get the current transform value to determine scroll position
      const transform = sliderElement.style.transform
      const translateX = transform ? parseFloat(transform.match(/-?\d+\.?\d*/)?.[0] || 0) : 0
      
      const containerWidth = sliderElement.offsetWidth
      const itemWidth = containerWidth / visibleProducts
      
      // Calculate which page we're currently viewing
      const currentPage = Math.round(Math.abs(translateX) / itemWidth)
      const clampedPage = Math.min(currentPage, totalPages - 1)
      
      if (clampedPage !== activePage) {
        setActivePage(clampedPage)
        setActiveProduct(clampedPage)
      }
    }

    // Add scroll listener with throttling
    let ticking = false
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    // Listen to slider container scroll events
    if (sliderElement) {
      sliderElement.addEventListener('scroll', throttledScroll)
    }

    return () => {
      if (sliderElement) {
        sliderElement.removeEventListener('scroll', throttledScroll)
      }
    }
  }, [activePage, totalPages, visibleProducts])



  if (!items || items.length === 0) {
    return null
  }

  return (
    <div className="product-slider">
      {totalPages > 1 && (
        <>
          <button
            className={`product-slider__arrow product-slider__arrow--left ${
              activePage === 0 ? 'product-slider__arrow--disabled' : ''
            }`}
            onClick={() => activePage > 0 && goToPage(activePage - 1)}
            disabled={activePage === 0}
            aria-label="Previous page"
          >
            <span className="product-slider__arrow-icon"/>
          </button>
          
          <button
            className={`product-slider__arrow product-slider__arrow--right ${
              activePage === totalPages - 1 ? 'product-slider__arrow--disabled' : ''
            }`}
            onClick={() => activePage < totalPages - 1 && goToPage(activePage + 1)}
            disabled={activePage === totalPages - 1}
            aria-label="Next page"
          >
            <span className="product-slider__arrow-icon"/>
          </button>
        </>
      )}

      <div className="product-slider__container">
        <div 
          className="product-slider__wrapper"
          ref={sliderRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {items.map((product, index) => (
            <div 
              key={`${product.id || product.contentful_id}-${index}`}
              className="product-slider__item"
              ref={(el) => (productsRef.current[index] = el)}
            >
              <div className="product-card">
                {product.image && (
                  <div className="product-card__image">
                    {product.productOverlay?.productOverlay ? (
                      <a 
                        href={product.productOverlay.productOverlay}
                        className="product-card__image-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View ${product.title?.title || product.internalName} details`}
                      >
                        {product.image.gatsbyImageData ? (
                          <GatsbyImage
                            image={product.image.gatsbyImageData}
                            alt={product.title?.title || product.image.title}
                            title={product.title?.title || product.image.title}
                            className="product-card__img"
                            loading={index < 4 ? 'eager' : 'lazy'}
                          />
                        ) : (
                          <img
                            src={product.image.file.url}
                            alt={product.title?.title || product.image.title}
                            title={product.title?.title || product.image.title}
                            className="product-card__img"
                            loading={index < 4 ? 'eager' : 'lazy'}
                          />
                        )}
                      </a>
                    ) : (
                      <>
                        {product.image.gatsbyImageData ? (
                          <GatsbyImage
                            image={product.image.gatsbyImageData}
                            alt={product.title?.title || product.image.title}
                            title={product.title?.title || product.image.title}
                            className="product-card__img"
                            loading={index < 4 ? 'eager' : 'lazy'}
                          />
                        ) : (
                          <img
                            src={product.image.file.url}
                            alt={product.title?.title || product.image.title}
                            title={product.title?.title || product.image.title}
                            className="product-card__img"
                            loading={index < 4 ? 'eager' : 'lazy'}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}

                {product.title?.title && (
                  <div className="product-card__content">
                    <h3 className="product-card__title">
                      {product.productOverlay?.productOverlay ? (
                        <a 
                          href={product.productOverlay.productOverlay}
                          className="product-card__link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {product.title.title}
                        </a>
                      ) : (
                        product.title.title
                      )}
                    </h3>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="product-slider__navigation">
            {Array.from({ length: totalPages }, (_, index) => {
              // Determine dot size based on whether there's more content to navigate to
              let sizeClass = ''
              
              // Show smaller dots on the RIGHT when we can slide right (not at the last page)
              if (activePage < totalPages - 1) {
                if (index === totalPages - 1) {
                  sizeClass = 'product-slider__dot--small' // Last dot 50% smaller
                } else if (index === totalPages - 2 && totalPages > 2) {
                  sizeClass = 'product-slider__dot--medium' // Second to last 25% smaller
                }
              }
              
              // Show smaller dots on the LEFT when we can slide left (not at the first page)
              if (activePage > 0) {
                if (index === 0) {
                  sizeClass = 'product-slider__dot--small' // First dot 50% smaller
                } else if (index === 1 && totalPages > 2) {
                  sizeClass = 'product-slider__dot--medium' // Second dot 25% smaller
                }
              }

              return (
                <button
                  key={`dot-${index}`}
                  className={`product-slider__dot ${
                    index === activePage ? 'product-slider__dot--active' : ''
                  } ${sizeClass}`}
                  onClick={() => goToPage(index)}
                  aria-label={`Go to page ${index + 1} of ${totalPages}`}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductSlider 